import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { API_URL as API } from '../config/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  // Messages traduits
  const messages = {
    fr: { invalidSession: 'Session invalide. Veuillez réessayer.', success: 'Connexion réussie !', error: 'Erreur de connexion. Veuillez réessayer.' },
    en: { invalidSession: 'Invalid session. Please try again.', success: 'Login successful!', error: 'Login error. Please try again.' },
    ht: { invalidSession: 'Sesyon envalid. Tanpri eseye ankò.', success: 'Koneksyon reyisi!', error: 'Erè koneksyon. Tanpri eseye ankò.' },
    es: { invalidSession: 'Sesión inválida. Por favor intente de nuevo.', success: '¡Inicio de sesión exitoso!', error: 'Error de inicio de sesión. Por favor intente de nuevo.' },
    pt: { invalidSession: 'Sessão inválida. Por favor tente novamente.', success: 'Login realizado com sucesso!', error: 'Erro de login. Por favor tente novamente.' }
  };

  const msg = messages[i18n.language] || messages.fr;

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      console.log('[AuthCallback] Starting session processing...');
      console.log('[AuthCallback] Current URL:', window.location.href);
      console.log('[AuthCallback] Hash:', location.hash);
      
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        console.error('[AuthCallback] No session_id found in hash');
        toast.error(msg.invalidSession);
        navigate('/auth');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log('[AuthCallback] Session ID extracted:', sessionId.substring(0, 20) + '...');

      try {
        console.log('[AuthCallback] Calling backend /api/auth/session...');
        const response = await axios.post(
          `${API}/auth/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true,
          }
        );

        console.log('[AuthCallback] Backend response OK');
        console.log('[AuthCallback] User:', response.data.email);
        
        setUser(response.data);
        console.log('[AuthCallback] Calling checkAuth...');
        await checkAuth();
        console.log('[AuthCallback] Context updated, redirecting to dashboard...');
        
        toast.success(msg.success);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('[AuthCallback] Session processing failed:', error);
        console.error('[AuthCallback] Error details:', error.response?.data);
        toast.error(msg.error);
        navigate('/auth');
      }
    };

    processSession();
  }, [location, navigate, setUser, checkAuth, msg]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-page" style={{ background: 'linear-gradient(135deg, #004E89 0%, #2A9D8F 100%)' }}>
      <div className="text-center bg-white rounded-3xl p-12 shadow-2xl">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6">
          <span className="text-white font-bold text-4xl">E</span>
        </div>
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mx-auto mb-4"></div>
        <p className="text-xl text-gray-800 font-semibold mb-2">Authentification en cours...</p>
        <p className="text-sm text-gray-600">Finalisation de votre connexion Google</p>
      </div>
    </div>
  );
};

export default AuthCallback;